// Web push in this browser: ask permission, get the browser's private delivery address and
// hand it to the server (which keeps it against the logged-in user). The notification itself
// is shown by public/sw.js.
import { getVapidKey, savePushSubscription, removePushSubscription } from "./api";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// An iPhone or iPad only receives push from the app added to the Home Screen, not from a Safari tab.
export function iosNeedsInstall() {
  const ua = navigator.userAgent || "";
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const installed =
    navigator.standalone === true ||
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches);
  return ios && !installed;
}

function keyToBytes(base64Url) {
  const padded = base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

// Subscribes this browser and tells the server. Saving the same address again is harmless.
async function subscribeAndSave() {
  const { key } = await getVapidKey();
  if (!key) return false;
  await navigator.serviceWorker.register("/sw.js");
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyToBytes(key),
  });
  const { endpoint, keys } = subscription.toJSON();
  await savePushSubscription({ endpoint, keys });
  return true;
}

/** Turns notifications on from a click. "enabled", "denied" or "unsupported". */
export async function enablePush() {
  if (!pushSupported() || iosNeedsInstall()) return "unsupported";
  if ((await Notification.requestPermission()) !== "granted") return "denied";
  return (await subscribeAndSave()) ? "enabled" : "unsupported";
}

/** At login: if the person already allowed notifications, keep this device's address fresh. */
export async function refreshPush() {
  if (!pushSupported() || Notification.permission !== "granted") return;
  try {
    await subscribeAndSave();
  } catch {
    // Offline or not set up yet: nothing to tell the person.
  }
}

/** At logout: the next person on this browser must not receive this person's notifications. */
export async function disablePush() {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    await removePushSubscription(subscription.endpoint);
    await subscription.unsubscribe();
  } catch {
    // The server also drops addresses the push service reports as gone.
  }
}

/** For the Enable button: "unsupported", "needs-install", "denied", "on" or "off". */
export async function pushStatus() {
  if (!pushSupported()) return iosNeedsInstall() ? "needs-install" : "unsupported";
  if (iosNeedsInstall()) return "needs-install";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "off";
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ? "on" : "off";
}
